/**
 * 計算ロジック（SPEC §6）。**環境非依存の純関数のみ。**
 *
 * フロントエンドと通知 Worker（Phase 5）の両方が import する。DOM・Workers 固有 API・
 * `fetch`・グローバル状態・`Date` に依存しない（CLAUDE.md 原則3 / SPEC §2.4-2）。
 * 「今日」は必ず引数で受け取る。これにより通知の予測とアプリ表示が原理的にズレない。
 */
import {
  addDays,
  daysInclusive,
  minDate,
  monthEnd,
  monthOf,
  type BusinessDate,
  type MonthKey,
} from './date';
import { sortPurchasesFifo, unopenedStock } from './fifo';
import type { ItemRow, LotRow, PurchaseRow } from './types';

/** 平均使用日数を出すのに使う確定ロットの件数（SPEC §6.1 / §6.3） */
export const RECENT_LOT_SAMPLE = 5;

/** 買い物リストのしきい値の既定（SPEC §6.4） */
export const DEFAULT_SHOPPING_THRESHOLD_DAYS = 7;

/** カテゴリ未設定の集計ラベル */
export const UNCATEGORIZED = '未分類';

export interface Dataset {
  items: ItemRow[];
  purchases: PurchaseRow[];
  lots: LotRow[];
}

/* ------------------------------------------------------------------ */
/* インデックス                                                          */
/* ------------------------------------------------------------------ */

export interface CalcIndex {
  items: ItemRow[];
  itemById: Map<string, ItemRow>;
  purchasesByItem: Map<string, PurchaseRow[]>;
  lotsByItem: Map<string, LotRow[]>;
  /** 単価 = total_price / qty（SPEC §4.3。float のまま保持し、表示時に丸める） */
  unitPriceByPurchase: Map<string, number>;
  /** 品目の全購入平均単価。purchase_id が NULL のロットに使う（SPEC §4.3） */
  avgUnitPriceByItem: Map<string, number>;
}

export function buildIndex(data: Dataset): CalcIndex {
  const items = data.items.filter((i) => i.deleted_at === null);
  const purchases = data.purchases.filter((p) => p.deleted_at === null);
  const lots = data.lots.filter((l) => l.deleted_at === null);

  const itemById = new Map(items.map((i) => [i.id, i]));
  const purchasesByItem = new Map<string, PurchaseRow[]>();
  const lotsByItem = new Map<string, LotRow[]>();
  const unitPriceByPurchase = new Map<string, number>();
  const spent = new Map<string, { price: number; qty: number }>();

  for (const p of sortPurchasesFifo(purchases)) {
    push(purchasesByItem, p.item_id, p);
    unitPriceByPurchase.set(p.id, p.qty > 0 ? p.total_price / p.qty : 0);
    const acc = spent.get(p.item_id) ?? { price: 0, qty: 0 };
    acc.price += p.total_price;
    acc.qty += p.qty;
    spent.set(p.item_id, acc);
  }

  for (const l of lots) push(lotsByItem, l.item_id, l);
  for (const [itemId, list] of lotsByItem) {
    lotsByItem.set(
      itemId,
      list.slice().sort((a, b) => cmp(a.opened_at, b.opened_at) || cmp(a.created_at, b.created_at)),
    );
  }

  const avgUnitPriceByItem = new Map<string, number>();
  for (const [itemId, acc] of spent) {
    avgUnitPriceByItem.set(itemId, acc.qty > 0 ? acc.price / acc.qty : 0);
  }

  return { items, itemById, purchasesByItem, lotsByItem, unitPriceByPurchase, avgUnitPriceByItem };
}

/** ロット単価（SPEC §4.3）。購入未紐付けなら品目の全購入平均単価。 */
export function lotUnitPrice(index: CalcIndex, lot: LotRow): number {
  if (lot.purchase_id !== null) {
    const p = index.unitPriceByPurchase.get(lot.purchase_id);
    if (p !== undefined) return p;
  }
  return index.avgUnitPriceByItem.get(lot.item_id) ?? 0;
}

/* ------------------------------------------------------------------ */
/* 平均使用日数（SPEC §6.1 / §6.3）                                      */
/* ------------------------------------------------------------------ */

/** 確定ロットの使用日数を新しい順に最大 limit 件 */
export function recentDurations(index: CalcIndex, itemId: string, limit = RECENT_LOT_SAMPLE): number[] {
  return (index.lotsByItem.get(itemId) ?? [])
    .filter((l): l is LotRow & { finished_at: string } => l.finished_at !== null)
    .sort((a, b) => cmp(b.finished_at, a.finished_at) || cmp(b.created_at, a.created_at))
    .slice(0, limit)
    .map((l) => daysInclusive(l.opened_at, l.finished_at));
}

export interface DurationEstimate {
  /** 平均使用日数（float のまま。日付に落とすときだけ整数化する） */
  days: number;
  /** history = 確定ロットの実績から / default = items.default_duration_days */
  basis: 'history' | 'default';
  /** 平均の元になった確定ロット件数 */
  sample: number;
}

export function averageDurationDays(index: CalcIndex, itemId: string): DurationEstimate {
  const durations = recentDurations(index, itemId);
  if (durations.length > 0) {
    const sum = durations.reduce((a, b) => a + b, 0);
    return { days: sum / durations.length, basis: 'history', sample: durations.length };
  }
  const fallback = index.itemById.get(itemId)?.default_duration_days ?? 30;
  return { days: fallback, basis: 'default', sample: 0 };
}

/* ------------------------------------------------------------------ */
/* 按分の対象期間（SPEC §6.1）                                           */
/* ------------------------------------------------------------------ */

export interface LotPeriod {
  lotId: string;
  itemId: string;
  start: BusinessDate;
  end: BusinessDate;
  /** 両端を含む使用日数。同日開封・使い切りなら 1。 */
  days: number;
  /** true = 使用中ロットの推定期間（SPEC §6.1「推定」バッジ） */
  estimated: boolean;
  unitPrice: number;
  /** 日割単価 = ロット単価 ÷ 使用日数 */
  dailyCost: number;
}

/**
 * ロット 1 本の按分対象期間。
 *
 * 確定（finished_at あり）: opened_at 〜 finished_at
 * 使用中: opened_at 〜 推定終了日。推定終了日が今日より前になる場合は
 *         「今日+1日」へ補正する（SPEC §6.1。過去に終わる予定のまま
 *         使い続けている矛盾を避ける）。
 */
export function lotPeriod(index: CalcIndex, lot: LotRow, today: BusinessDate): LotPeriod {
  const unitPrice = lotUnitPrice(index, lot);

  let end: BusinessDate;
  let estimated: boolean;
  if (lot.finished_at !== null) {
    end = lot.finished_at;
    estimated = false;
  } else {
    const estDays = Math.max(1, Math.round(averageDurationDays(index, lot.item_id).days));
    const naiveEnd = addDays(lot.opened_at, estDays - 1);
    end = naiveEnd < today ? addDays(today, 1) : naiveEnd;
    estimated = true;
  }

  const days = Math.max(1, daysInclusive(lot.opened_at, end));
  return {
    lotId: lot.id,
    itemId: lot.item_id,
    start: lot.opened_at,
    end,
    days,
    estimated,
    unitPrice,
    dailyCost: unitPrice / days,
  };
}

/** 期間を月ごとの日数に分割する。両端の月は日割りになる。 */
export function splitByMonth(start: BusinessDate, end: BusinessDate): Array<{ month: MonthKey; days: number }> {
  if (end < start) return [];
  const out: Array<{ month: MonthKey; days: number }> = [];
  let cursor = start;
  while (cursor <= end) {
    const month = monthOf(cursor);
    const stop = minDate(monthEnd(month), end);
    out.push({ month, days: daysInclusive(cursor, stop) });
    cursor = addDays(stop, 1);
  }
  return out;
}

/** ロット 1 本を月別費用へ按分する（SPEC §6.1） */
export function allocateLot(period: LotPeriod): Array<{ month: MonthKey; amount: number }> {
  return splitByMonth(period.start, period.end).map((s) => ({
    month: s.month,
    amount: period.dailyCost * s.days,
  }));
}

/* ------------------------------------------------------------------ */
/* 月次集計                                                             */
/* ------------------------------------------------------------------ */

export interface ItemMonthCost {
  itemId: string;
  name: string;
  category: string | null;
  confirmed: number;
  estimated: number;
  total: number;
}

export interface MonthSummary {
  month: MonthKey;
  /** 発生主義（確定 + 推定） */
  accrual: number;
  accrualConfirmed: number;
  accrualEstimated: number;
  /** 現金主義（SPEC §6.2） */
  cash: number;
  byCategory: Record<string, number>;
  /** total 降順 */
  byItem: ItemMonthCost[];
}

/**
 * 指定した月の一覧について発生主義・現金主義を集計する（SPEC §6.1 / §6.2 / §7.3）。
 * `months` に無い月への按分は捨てる。
 */
export function monthlySummaries(data: Dataset, today: BusinessDate, months: MonthKey[]): MonthSummary[] {
  const index = buildIndex(data);
  const wanted = new Set(months);
  const buckets = new Map<MonthKey, MonthSummary>(
    months.map((m) => [
      m,
      { month: m, accrual: 0, accrualConfirmed: 0, accrualEstimated: 0, cash: 0, byCategory: {}, byItem: [] },
    ]),
  );
  const itemCosts = new Map<MonthKey, Map<string, ItemMonthCost>>(months.map((m) => [m, new Map()]));

  for (const [itemId, lots] of index.lotsByItem) {
    const item = index.itemById.get(itemId);
    if (!item) continue;
    for (const lot of lots) {
      const period = lotPeriod(index, lot, today);
      for (const part of allocateLot(period)) {
        if (!wanted.has(part.month)) continue;
        const bucket = buckets.get(part.month)!;
        bucket.accrual += part.amount;
        if (period.estimated) bucket.accrualEstimated += part.amount;
        else bucket.accrualConfirmed += part.amount;

        const key = item.category ?? UNCATEGORIZED;
        bucket.byCategory[key] = (bucket.byCategory[key] ?? 0) + part.amount;

        const perItem = itemCosts.get(part.month)!;
        const row = perItem.get(itemId) ?? {
          itemId,
          name: item.name,
          category: item.category,
          confirmed: 0,
          estimated: 0,
          total: 0,
        };
        if (period.estimated) row.estimated += part.amount;
        else row.confirmed += part.amount;
        row.total += part.amount;
        perItem.set(itemId, row);
      }
    }
  }

  for (const p of data.purchases) {
    if (p.deleted_at !== null) continue;
    const m = monthOf(p.purchased_at);
    if (!wanted.has(m)) continue;
    buckets.get(m)!.cash += p.total_price;
  }

  for (const m of months) {
    buckets.get(m)!.byItem = [...itemCosts.get(m)!.values()].sort((a, b) => b.total - a.total);
  }

  return months.map((m) => buckets.get(m)!);
}

/** 直近 count ヶ月の発生主義費用の単純平均（SPEC §7.3） */
export function averageAccrual(summaries: MonthSummary[], count: number): number {
  const slice = summaries.slice(-count);
  if (slice.length === 0) return 0;
  return slice.reduce((a, s) => a + s.accrual, 0) / slice.length;
}

/* ------------------------------------------------------------------ */
/* 在庫切れ予測（SPEC §6.3）                                             */
/* ------------------------------------------------------------------ */

export interface Forecast {
  itemId: string;
  /** 平均使用日数 D */
  averageDurationDays: number;
  basis: 'history' | 'default';
  sample: number;
  openLotCount: number;
  unopenedStock: number;
  /** あと何日で在庫切れか（表示用に整数化済み） */
  daysLeft: number;
  runsOutOn: BusinessDate;
}

/**
 * 在庫切れ予測日 = 今日 + 使用中ロットの残り日数 + 未開封在庫数 × D。
 *
 * 使用中ロットが複数ある場合は残り日数を合算する（順に使い切る前提。
 * SPEC §6.3 は 1 本を想定した書き方だが、複数開封は §7.1 で許容されている）。
 */
export function forecast(index: CalcIndex, itemId: string, today: BusinessDate): Forecast {
  const est = averageDurationDays(index, itemId);
  const lots = index.lotsByItem.get(itemId) ?? [];
  const purchases = index.purchasesByItem.get(itemId) ?? [];
  const openLots = lots.filter((l) => l.finished_at === null);

  let remaining = 0;
  for (const lot of openLots) {
    const elapsed = daysInclusive(lot.opened_at, today);
    remaining += Math.max(0, est.days - elapsed);
  }

  const stock = Math.max(0, unopenedStock(purchases, lots, itemId));
  const daysLeft = Math.max(0, Math.round(remaining + stock * est.days));

  return {
    itemId,
    averageDurationDays: est.days,
    basis: est.basis,
    sample: est.sample,
    openLotCount: openLots.length,
    unopenedStock: stock,
    daysLeft,
    runsOutOn: addDays(today, daysLeft),
  };
}

/* ------------------------------------------------------------------ */
/* 買い物リスト（SPEC §6.4）                                             */
/* ------------------------------------------------------------------ */

export interface ShoppingCandidate extends Forecast {
  name: string;
  category: string | null;
}

/**
 * 在庫切れ予測がしきい値以下の品目（あと何日が少ない順）。
 * 手動追加・除外は UI 側の関心事なので、ここでは自動掲載分だけを返す。
 */
export function shoppingList(
  data: Dataset,
  today: BusinessDate,
  thresholdDays = DEFAULT_SHOPPING_THRESHOLD_DAYS,
): ShoppingCandidate[] {
  const index = buildIndex(data);
  return index.items
    .map((item) => ({ ...forecast(index, item.id, today), name: item.name, category: item.category }))
    .filter((f) => f.daysLeft <= thresholdDays)
    .sort((a, b) => a.daysLeft - b.daysLeft || cmp(a.name, b.name));
}

/* ------------------------------------------------------------------ */
/* 価格履歴（SPEC §6.5）                                                 */
/* ------------------------------------------------------------------ */

export interface PricePoint {
  purchaseId: string;
  purchasedAt: BusinessDate;
  unitPrice: number;
  qty: number;
  totalPrice: number;
  store: string | null;
}

export interface PriceHistory {
  /** purchased_at 昇順 */
  points: PricePoint[];
  latest: number | null;
  previous: number | null;
  /** (latest − previous) / previous。前回が無い / 0 なら null */
  changeRatio: number | null;
}

export function priceHistory(index: CalcIndex, itemId: string): PriceHistory {
  const points: PricePoint[] = (index.purchasesByItem.get(itemId) ?? []).map((p) => ({
    purchaseId: p.id,
    purchasedAt: p.purchased_at,
    unitPrice: index.unitPriceByPurchase.get(p.id) ?? 0,
    qty: p.qty,
    totalPrice: p.total_price,
    store: p.store,
  }));

  const latest = points.length > 0 ? points[points.length - 1].unitPrice : null;
  const previous = points.length > 1 ? points[points.length - 2].unitPrice : null;
  const changeRatio =
    latest !== null && previous !== null && previous !== 0 ? (latest - previous) / previous : null;

  return { points, latest, previous, changeRatio };
}

/* ------------------------------------------------------------------ */
/* CSV 行（SPEC §9.2）                                                   */
/* ------------------------------------------------------------------ */

export interface CsvRow {
  month: MonthKey;
  category: string;
  accrual_cost: number;
  cash_cost: number;
}

/**
 * `month,category,accrual_cost,cash_cost` の行データ。
 * 現金主義は購入の発生した月・その品目のカテゴリへ計上する。
 */
export function monthlyCsvRows(data: Dataset, today: BusinessDate, months: MonthKey[]): CsvRow[] {
  const index = buildIndex(data);
  const summaries = monthlySummaries(data, today, months);
  const wanted = new Set(months);

  // カテゴリ名がどんな文字でも壊れないよう、月 -> カテゴリ の入れ子で持つ
  const cashByMonth = new Map<MonthKey, Map<string, number>>(months.map((m) => [m, new Map()]));
  for (const p of data.purchases) {
    if (p.deleted_at !== null) continue;
    const m = monthOf(p.purchased_at);
    if (!wanted.has(m)) continue;
    const category = index.itemById.get(p.item_id)?.category ?? UNCATEGORIZED;
    const perCategory = cashByMonth.get(m)!;
    perCategory.set(category, (perCategory.get(category) ?? 0) + p.total_price);
  }

  const rows: CsvRow[] = [];
  for (const s of summaries) {
    const cashOfMonth = cashByMonth.get(s.month)!;
    const categories = new Set<string>([...Object.keys(s.byCategory), ...cashOfMonth.keys()]);
    for (const category of [...categories].sort()) {
      const accrual_cost = Math.round(s.byCategory[category] ?? 0);
      const cash_cost = cashOfMonth.get(category) ?? 0;
      if (accrual_cost === 0 && cash_cost === 0) continue;
      rows.push({ month: s.month, category, accrual_cost, cash_cost });
    }
  }
  return rows;
}

/* ------------------------------------------------------------------ */

function push<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);
